// 文字列を「書記素クラスタ（grapheme cluster）」単位で扱うユーティリティ。
//
// エディタは本来 UTF-16 コードユニット基準でインデックス管理していたが、
// 「⁉︎」(U+2049 + 異体字セレクタ U+FE0E) やサロゲートペア(𠮷 U+20BB7 等)のように
// 1つの見た目の文字（書記素）が複数コードユニットで構成される環境依存文字では、
// 「1コードユニット = 1表示セル = 1インデックス」という前提が崩れ、表示・カーソル移動が壊れる。
//
// そこでエディタ内部の論理インデックス（data-index / wordIndex / style.start_index 等）は
// すべて「書記素序数」で統一し、UTF-16 文字列を実際に slice する境界や DB 永続値との境界でのみ
// このモジュールの変換関数でコードユニット位置へ変換する。
const graphemeSegmenter =
  typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : null;

// 直近に分割した文字列の結果をキャッシュする（countProtrusion などから同一 body が
// 繰り返し分割されるため）。文字列は不変なのでキー＝文字列で安全にキャッシュできる。
const _cache = new Map();
const _CACHE_MAX = 256;

function rawSegments(str) {
  if (graphemeSegmenter) {
    return Array.from(graphemeSegmenter.segment(str), (seg) => ({
      word: seg.segment,
      index: seg.index,
    }));
  }
  // フォールバック: Intl.Segmenter 非対応環境ではコードポイント単位で分割する。
  // 書記素クラスタの結合はできないが、コードユニット位置(index)は正しく維持する。
  const result = [];
  let index = 0;
  for (const codePoint of str) {
    result.push({ word: codePoint, index });
    index += codePoint.length;
  }
  return result;
}

/**
 * 文字列を書記素クラスタ単位に分割する（キャッシュ付き）。
 * @param {string} str 対象文字列
 * @returns {{ word: string, index: number }[]} クラスタ文字列と、その先頭のコードユニット位置
 */
export function segmentGraphemes(str) {
  if (str == null || str === '') return [];
  const cached = _cache.get(str);
  if (cached) return cached;
  const segs = rawSegments(str);
  if (_cache.size >= _CACHE_MAX) _cache.clear();
  _cache.set(str, segs);
  return segs;
}

/**
 * 書記素クラスタの文字列配列を返す。
 * @param {string} str
 * @returns {string[]}
 */
export function graphemesOf(str) {
  return segmentGraphemes(str).map((seg) => seg.word);
}

/**
 * 書記素数（＝論理的な文字数）を返す。文字列の `.length`（コードユニット数）の代替。
 * @param {string} str
 * @returns {number}
 */
export function graphemeCount(str) {
  if (str == null || str === '') return 0;
  return segmentGraphemes(str).length;
}

/**
 * 書記素序数を基準に文字列を slice する。`str.slice(start, end)` の書記素版。
 * @param {string} str
 * @param {number} [start=0] 開始書記素序数
 * @param {number} [end] 終了書記素序数（省略時は末尾まで）
 * @returns {string}
 */
export function sliceGraphemes(str, start = 0, end) {
  if (str == null || str === '') return '';
  const segs = segmentGraphemes(str);
  const s = start < 0 ? Math.max(0, segs.length + start) : start;
  const e = end == null ? segs.length : end < 0 ? Math.max(0, segs.length + end) : end;
  const cuStart = s >= segs.length ? str.length : segs[s].index;
  const cuEnd = e >= segs.length ? str.length : segs[e].index;
  return str.slice(cuStart, Math.max(cuStart, cuEnd));
}

/**
 * 書記素序数 → コードユニット位置。
 * @param {string} str
 * @param {number} graphemeIndex 書記素序数
 * @returns {number} コードユニット位置
 */
export function graphemeToCodeUnit(str, graphemeIndex) {
  if (str == null || str === '') return 0;
  const segs = segmentGraphemes(str);
  if (graphemeIndex <= 0) return 0;
  if (graphemeIndex >= segs.length) return str.length;
  return segs[graphemeIndex].index;
}

/**
 * コードユニット位置 → 書記素序数。
 * 境界に一致すればその序数、書記素の途中なら直後の境界へスナップする。
 * @param {string} str
 * @param {number} codeUnitIndex コードユニット位置
 * @returns {number} 書記素序数
 */
export function codeUnitToGrapheme(str, codeUnitIndex) {
  if (str == null || str === '') return 0;
  const segs = segmentGraphemes(str);
  if (codeUnitIndex <= 0) return 0;
  if (codeUnitIndex >= str.length) return segs.length;
  for (let i = 0; i < segs.length; i++) {
    if (segs[i].index >= codeUnitIndex) return i;
  }
  return segs.length;
}

export function countGraphemes(text) {
  // 'grapheme'（書記素）単位で分割するセグメンターを作成
  const segmenter = new Intl.Segmenter('ja', { granularity: 'grapheme' });

  // 文字列をセグメントに分解し、その個数をカウント
  return [...segmenter.segment(text)].length;
}
